import React from 'react';
import { Card, Text, Button } from '@fragments';
import { IconBolt, IconHome, IconHeartHandshake, IconFlame, IconPlus } from '@tabler/icons-react';
import './styles.scss';

/**
 * SavedActions — a saved-payee / quick-action list card (AlignUI finance dashboard).
 *
 * Flat white surface on the gray canvas. The flush body owns an <ul> of saved
 * actions, each separated by a crisp full-width hairline (border-bottom on every
 * row but the last). A row leads with a neutral rounded chip — or, for a person,
 * an avatar-initials circle — carries a name + one-line description in a
 * min-width:0 column, and pushes the strong-ink amount to the right edge. The
 * header action is a "See All" primary text Button; the footer is a full-width
 * dashed "Save a New Action" Button.
 */

// ── Mock data (inline, self-contained) ──────────────────────────────────────
// `avatar` rows render an initials circle instead of an `Icon` chip.
const SAVED_ACTIONS = [
    {
        id: 'rent',
        name: 'Rent Payment',
        sub: 'Monthly rent payment.',
        amount: '$900.00',
        Icon: IconHome,
    },
    {
        id: 'tuition',
        name: "Natalia's Tuition",
        sub: "Nat's university fee.",
        amount: '$750.00',
        avatar: 'N',
    },
    {
        id: 'donation',
        name: 'Donation to TEMA',
        sub: 'In the name of our family.',
        amount: '$100.00',
        Icon: IconHeartHandshake,
    },
    {
        id: 'gas',
        name: 'Gas Bill Payment',
        sub: 'Monthly gas bill payment.',
        amount: '$20.00',
        Icon: IconFlame,
    },
];

export default function SavedActions() {
    return (
        <Card
            flat
            noBodyPadding
            title="Saved Actions"
            icon={<IconBolt />}
            action={
                <Button color="inverse" variant="text" size="small">
                    See All
                </Button>
            }
            footer={
                <Button
                    color="inverse"
                    variant="dashed"
                    size="small"
                    fullWidth
                    startIcon={<IconPlus />}
                >
                    Save a New Action
                </Button>
            }
        >
            <ul className="saved-actions">
                {SAVED_ACTIONS.map(({ id, name, sub, amount, Icon, avatar }) => (
                    <li key={id} className="saved-actions__row">
                        {avatar ? (
                            <span className="saved-actions__avatar" aria-hidden="true">
                                <Text.label contrast={6}>{avatar}</Text.label>
                            </span>
                        ) : (
                            <span className="saved-actions__chip" aria-hidden="true">
                                <Icon />
                            </span>
                        )}

                        <div className="saved-actions__info">
                            <Text.label contrast={9} maxLines={1}>
                                {name}
                            </Text.label>
                            <Text.caption contrast={6} maxLines={1}>
                                {sub}
                            </Text.caption>
                        </div>

                        <span className="saved-actions__amount">
                            <Text.label contrast={9} maxLines={1}>
                                {amount}
                            </Text.label>
                        </span>
                    </li>
                ))}
            </ul>
        </Card>
    );
}
